import { Container } from '@design-system/container';
import { FluidGrid } from '@design-system/fluid-grid';
import { PageBlock } from '@design-system/page-block';
import { Typography } from '@design-system/typography';
import { ScreenRecognitionContext } from '@phx-husky/screen-recognition';
import React, { FC, forwardRef, useContext } from 'react';

import { useDisclosure } from '~/hooks/useDisclosure';
import { useItemFilters } from '~/hooks/useFilter';
import { mapItems, wrapIfItsList } from '~/mappers/itemListing';
import { ItemListingTypes } from '~/mappers/itemListing/models';

import { ItemListingProps } from '../models';
import ItemListingDefaultFilter from './ItemListingDefaultFilter';
import ItemListingDefaultSortBy from './ItemListingDefaultSortBy';

export const ItemListingDefaultWithFilters: FC<
  Omit<ItemListingProps, 'type'>
> = ({
  pageBlock,
  fluidGrid,
  id,
  filterSettings: {
    filters,
    clearSelectedFilters,
    itemsName,
    sortBy,
    noResults: { text, typographyUI },
  },
  rawItems,
  currentPageUid,
  uid,
}) => {
  const {
    data,
    filter,
    handleFilterChange,
    sort,
    handleSortChange,
    getActiveCountForGroup,
    handleGroupReset,
    handleActiveGroup,
    activeGroup,
  } = useItemFilters(rawItems.items, {
    fields: {
      name: 'fields.productData.productTitle',
      createdAt: 'created_at',
    },
    filters,
  });
  const { isOpen: isSortingOpen, toggle: toggleSortingOpen } =
    useDisclosure(false);
  const { isMobile } = useContext(ScreenRecognitionContext);

  const itemsAmount = data.length;

  return (
    <>
      <ItemListingDefaultFilter
        {...{
          filters,
          clearSelectedFilters,
          getActiveCountForGroup,
          handleFilterChange,
          handleGroupReset,
          filter,
          activeGroup,
          handleActiveGroup,
          isMobile,
        }}
      />
      <ItemListingDefaultSortBy
        {...{
          handleSortChange,
          itemsAmount,
          isSortingOpen,
          toggleSortingOpen,
          sort,
          itemsName,
          sortBy,
        }}
      />
      {itemsAmount ? (
        <PageBlock
          {...pageBlock}
          className="item-listing-default-block"
          id={id}
          componentUid={uid}
        >
          <FluidGrid {...fluidGrid}>
            {wrapIfItsList({
              tag: fluidGrid.tag as string,
              items: mapItems(
                { ...rawItems, items: data } as ItemListingTypes,
                currentPageUid
              ),
            })}
          </FluidGrid>
        </PageBlock>
      ) : (
        <Container maxWidth="full-width" className="filter-no-results">
          <Typography
            content={text}
            variant={typographyUI.variant}
            size={typographyUI.size}
            weight={typographyUI.weight}
            tag={typographyUI.tag}
            align={typographyUI.align}
            className="filter-no-results__text"
          />
        </Container>
      )}
    </>
  );
};

export const ItemListingDefault = forwardRef<
  HTMLDivElement,
  Omit<ItemListingProps, 'type'>
>((props, ref) => {
  const { pageBlock, fluidGrid, items, id, filterSettings, uid } = props;

  return filterSettings ? (
    <ItemListingDefaultWithFilters {...props} />
  ) : (
    <PageBlock
      {...pageBlock}
      className="item-listing-default-block"
      id={id}
      componentUid={uid}
      ref={ref}
    >
      <FluidGrid {...fluidGrid}>{items}</FluidGrid>
    </PageBlock>
  );
});

export default ItemListingDefault;
